<script setup>
// Meta Info — perfil comercial do número WhatsApp Cloud na Meta (mesmos campos
// do WhatsApp Manager): foto, nome de exibição, categoria, descrição, endereço,
// e-mail e sites. Leitura/edição ao vivo via Graph API.
import { ref, computed, onMounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import MetaInfoAPI from 'dashboard/api/metaInfo';

const props = defineProps({
  inbox: {
    type: Object,
    default: () => ({}),
  },
});

const isLoading = ref(true);
const loadError = ref('');
const isSaving = ref(false);
const isUploading = ref(false);
const isRequestingName = ref(false);

const phone = ref({});
const profilePictureUrl = ref('');
const about = ref('');
const description = ref('');
const address = ref('');
const email = ref('');
const vertical = ref('');
const website1 = ref('');
const website2 = ref('');
const newDisplayName = ref('');
const fileInput = ref(null);

const VERTICALS = [
  { value: 'OTHER', label: 'Outro' },
  { value: 'AUTO', label: 'Automotivo' },
  { value: 'BEAUTY', label: 'Beleza, spa e salão' },
  { value: 'APPAREL', label: 'Vestuário e moda' },
  { value: 'EDU', label: 'Educação' },
  { value: 'ENTERTAIN', label: 'Entretenimento' },
  { value: 'EVENT_PLAN', label: 'Planejamento de eventos' },
  { value: 'FINANCE', label: 'Finanças e bancos' },
  { value: 'GROCERY', label: 'Mercado e alimentação' },
  { value: 'GOVT', label: 'Serviço público' },
  { value: 'HOTEL', label: 'Hotel e hospedagem' },
  { value: 'HEALTH', label: 'Saúde e medicina' },
  { value: 'NONPROFIT', label: 'Organização sem fins lucrativos' },
  { value: 'PROF_SERVICES', label: 'Serviços profissionais' },
  { value: 'RETAIL', label: 'Varejo e compras' },
  { value: 'TRAVEL', label: 'Viagens e transporte' },
  { value: 'RESTAURANT', label: 'Restaurante' },
  { value: 'NOT_A_BIZ', label: 'Não é uma empresa' },
];

const NAME_STATUS_LABEL = {
  APPROVED: 'Aprovado',
  AVAILABLE_WITHOUT_REVIEW: 'Disponível sem análise',
  PENDING_REVIEW: 'Em análise pela Meta',
  DECLINED: 'Recusado pela Meta',
  EXPIRED: 'Certificado expirado',
  NONE: '—',
};

const qualityColor = computed(() => {
  const q = (phone.value.quality_rating || '').toUpperCase();
  if (q === 'GREEN') return 'bg-n-teal-9';
  if (q === 'YELLOW') return 'bg-n-amber-9';
  if (q === 'RED') return 'bg-n-ruby-9';
  return 'bg-n-slate-9';
});

const nameStatusLabel = computed(
  () => NAME_STATUS_LABEL[phone.value.name_status] || phone.value.name_status || '—'
);

const load = async () => {
  isLoading.value = true;
  loadError.value = '';
  try {
    const { data } = await MetaInfoAPI.get(props.inbox.id);
    const profile = data.profile || {};
    phone.value = data.phone || {};
    profilePictureUrl.value = profile.profile_picture_url || '';
    about.value = profile.about || '';
    description.value = profile.description || '';
    address.value = profile.address || '';
    email.value = profile.email || '';
    vertical.value = profile.vertical || 'OTHER';
    const sites = profile.websites || [];
    website1.value = sites[0] || '';
    website2.value = sites[1] || '';
  } catch (e) {
    loadError.value = e?.response?.data?.error || e.message;
  } finally {
    isLoading.value = false;
  }
};

const save = async () => {
  isSaving.value = true;
  try {
    await MetaInfoAPI.update(props.inbox.id, {
      about: about.value,
      description: description.value,
      address: address.value,
      email: email.value,
      vertical: vertical.value,
      websites: [website1.value, website2.value].filter(Boolean),
    });
    useAlert('Perfil atualizado na Meta.');
    load();
  } catch (e) {
    useAlert(e?.response?.data?.error || e.message);
  } finally {
    isSaving.value = false;
  }
};

const pickFile = () => fileInput.value?.click();

const onFileChange = async event => {
  const file = event.target.files?.[0];
  if (!file) return;
  isUploading.value = true;
  try {
    await MetaInfoAPI.uploadPicture(props.inbox.id, file);
    useAlert('Foto do perfil atualizada.');
    load();
  } catch (e) {
    useAlert(e?.response?.data?.error || e.message);
  } finally {
    isUploading.value = false;
    if (fileInput.value) fileInput.value.value = '';
  }
};

const requestName = async () => {
  if (!newDisplayName.value.trim()) return;
  isRequestingName.value = true;
  try {
    await MetaInfoAPI.requestDisplayName(
      props.inbox.id,
      newDisplayName.value.trim()
    );
    useAlert(
      'Solicitação enviada — o novo nome passa por análise da Meta antes de valer.'
    );
    newDisplayName.value = '';
    load();
  } catch (e) {
    useAlert(e?.response?.data?.error || e.message);
  } finally {
    isRequestingName.value = false;
  }
};

onMounted(load);
</script>

<template>
  <div class="mx-8 mt-4 mb-8 max-w-3xl">
    <div v-if="isLoading" class="text-sm text-n-slate-11 py-8">
      Carregando o perfil do número na Meta…
    </div>
    <div v-else-if="loadError" class="text-sm text-n-ruby-11 py-8">
      {{ loadError }}
    </div>
    <template v-else>
      <!-- Cabeçalho: foto + número + qualidade -->
      <div class="flex items-center gap-4 mb-6">
        <img
          v-if="profilePictureUrl"
          :src="profilePictureUrl"
          class="w-16 h-16 rounded-full object-cover border border-n-weak"
        />
        <div
          v-else
          class="w-16 h-16 rounded-full bg-n-slate-4 flex items-center justify-center text-n-slate-10 text-xl"
        >
          ☎
        </div>
        <div>
          <p class="text-heading-2 text-n-slate-12 mb-0.5">
            {{ phone.display_phone_number || '—' }}
          </p>
          <p class="text-sm text-n-slate-11">
            {{ phone.verified_name || '—' }} · ID do número:
            {{ phone.id || '—' }}
          </p>
          <p class="text-xs text-n-slate-11 flex items-center gap-1.5 mt-1">
            <span :class="`inline-block w-2.5 h-2.5 rounded-full ${qualityColor}`" />
            Qualidade: {{ phone.quality_rating || '—' }}
            <span v-if="phone.messaging_limit_tier">
              · Limite: {{ phone.messaging_limit_tier }}
            </span>
          </p>
        </div>
      </div>

      <!-- Foto do perfil -->
      <div class="mb-6">
        <p class="text-sm font-medium text-n-slate-12 mb-1">Foto do perfil</p>
        <p class="text-xs text-n-slate-11 mb-2">
          Essa imagem será exibida no seu perfil comercial (JPEG ou PNG).
        </p>
        <input
          ref="fileInput"
          type="file"
          accept="image/jpeg,image/png"
          class="hidden"
          @change="onFileChange"
        />
        <NextButton
          variant="outline"
          color="slate"
          :label="isUploading ? 'Enviando…' : 'Escolher arquivo'"
          :is-loading="isUploading"
          @click="pickFile"
        />
      </div>

      <!-- Nome de exibição -->
      <div class="mb-6">
        <p class="text-sm font-medium text-n-slate-12 mb-1">Nome de exibição</p>
        <p class="text-sm text-n-slate-12 mb-1">
          {{ phone.verified_name || '—' }}
          <span class="text-xs text-n-slate-11">({{ nameStatusLabel }})</span>
        </p>
        <p class="text-xs text-n-slate-11 mb-2">
          A alteração passa por análise da Meta. Após aprovada, a Meta pede o
          re-registro do número.
        </p>
        <div class="flex gap-2 items-center">
          <input
            v-model="newDisplayName"
            type="text"
            placeholder="Novo nome de exibição"
            class="!mb-0 max-w-xs text-sm"
          />
          <NextButton
            variant="outline"
            :label="isRequestingName ? 'Enviando…' : 'Solicitar alteração'"
            :is-loading="isRequestingName"
            :disabled="!newDisplayName.trim()"
            @click="requestName"
          />
        </div>
      </div>

      <!-- Informações da empresa -->
      <p class="text-sm font-medium text-n-slate-12 mb-1">
        Informações da empresa
      </p>
      <p class="text-xs text-n-slate-11 mb-3">
        Adicione alguns detalhes sobre sua empresa.
      </p>

      <label class="block text-xs text-n-slate-11 mb-1">Categoria</label>
      <select v-model="vertical" class="mb-4 max-w-md text-sm">
        <option v-for="v in VERTICALS" :key="v.value" :value="v.value">
          {{ v.label }}
        </option>
      </select>

      <label class="block text-xs text-n-slate-11 mb-1">
        Recado / Bio (about) · {{ about.length }}/139
      </label>
      <input v-model="about" maxlength="139" type="text" class="mb-4 text-sm" />

      <label class="block text-xs text-n-slate-11 mb-1">
        Descrição · {{ description.length }}/512
      </label>
      <textarea
        v-model="description"
        maxlength="512"
        rows="3"
        class="mb-4 text-sm"
      />

      <label class="block text-xs text-n-slate-11 mb-1">
        Endereço · {{ address.length }}/256
      </label>
      <input
        v-model="address"
        maxlength="256"
        type="text"
        placeholder="Insira o endereço comercial"
        class="mb-4 text-sm"
      />

      <label class="block text-xs text-n-slate-11 mb-1">
        Email · {{ email.length }}/128
      </label>
      <input
        v-model="email"
        maxlength="128"
        type="email"
        placeholder="Insira o email comercial"
        class="mb-4 text-sm"
      />

      <label class="block text-xs text-n-slate-11 mb-1">Site 1</label>
      <input
        v-model="website1"
        maxlength="256"
        type="url"
        placeholder="https://www.exemplo.com"
        class="mb-4 text-sm"
      />

      <label class="block text-xs text-n-slate-11 mb-1">Site 2</label>
      <input
        v-model="website2"
        maxlength="256"
        type="url"
        placeholder="https://www.exemplo.com"
        class="mb-6 text-sm"
      />

      <NextButton
        :label="isSaving ? 'Salvando…' : 'Salvar alterações'"
        :is-loading="isSaving"
        @click="save"
      />
    </template>
  </div>
</template>
